import { LUT_H, LUT_W, buildSkyTable } from './atmosphere';

// Sky scattering tables for any light altitude, from a cache of tables at fixed altitudes (0.5° apart in
// twilight, 2° higher up) blended geometrically. Missing tables are built in a worker; until they arrive
// the nearest cached one stands in, so time-lapses and scrubbing never stall the frame.

const N = LUT_W * LUT_H * 4;
const MAX_CACHED = 160;

/** Table altitudes (degrees): −20…10 every 0.5°, then 10…90 every 2°. */
function bracket(deg: number): [number, number, number] {
  const d = Math.max(-20, Math.min(90, deg));
  const step = d < 10 ? 0.5 : 2;
  const base = d < 10 ? -20 : 10;
  const k0 = base + Math.floor((d - base) / step) * step;
  const k1 = Math.min(90, k0 + step);
  return [k0, k1, k1 > k0 ? (d - k0) / (k1 - k0) : 0];
}

export class SkyTables {
  private cache = new Map<number, Float32Array>();
  private pending = new Set<number>();
  private worker: Worker | null = null;
  /** Bumped whenever a new table arrives, so callers know to re-blend. */
  version = 0;

  constructor() {
    try {
      this.worker = new Worker(new URL('./skytable.worker.ts', import.meta.url), { type: 'module' });
      this.worker.onmessage = (e: MessageEvent<{ key: number; table: Float32Array }>) => {
        this.pending.delete(e.data.key);
        this.put(e.data.key, e.data.table);
        this.version++;
      };
    } catch {
      this.worker = null; // build on the main thread instead
    }
  }

  private put(key: number, t: Float32Array) {
    this.cache.set(key, t);
    if (this.cache.size > MAX_CACHED) this.cache.delete(this.cache.keys().next().value!);
  }

  private request(key: number) {
    if (this.cache.has(key) || this.pending.has(key)) return;
    if (!this.worker) {
      this.put(key, buildSkyTable((key * Math.PI) / 180));
      return;
    }
    this.pending.add(key);
    this.worker.postMessage({ key });
  }

  private nearest(deg: number): Float32Array | null {
    let best: Float32Array | null = null, bd = Infinity;
    for (const [k, t] of this.cache) {
      const d = Math.abs(k - deg);
      if (d < bd) {
        bd = d;
        best = t;
      }
    }
    return best;
  }

  /** Blend the table for `altRad` into `out` (raw radiance); false if nothing is available yet. */
  blend(altRad: number, out: Float32Array, offset: number): boolean {
    const deg = (altRad * 180) / Math.PI;
    const [k0, k1, f] = bracket(deg);
    this.request(k0);
    this.request(k1);
    let a = this.cache.get(k0), b = this.cache.get(k1);
    if (!a && !b) {
      // First frames: build the lower table right away so the sky is never blank.
      if (!this.cache.size) {
        a = buildSkyTable((k0 * Math.PI) / 180);
        this.put(k0, a);
        this.pending.delete(k0);
      } else {
        a = this.nearest(deg) ?? undefined;
      }
    }
    if (!a || !b) {
      const t = (a ?? b)!;
      out.set(t, offset);
      return true;
    }
    // Radiance changes roughly exponentially with the Sun's altitude in twilight: interpolate logarithms.
    for (let i = 0; i < N; i++) {
      const x = a[i], y = b[i];
      out[offset + i] = x > 0 && y > 0 ? x * Math.pow(y / x, f) : x + (y - x) * f;
    }
    return true;
  }
}
